import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSouschef } from "@/hooks/useSouschef";
import SessionManager from "@/components/SessionManager";
import ChatView from "@/components/ChatView";
import RagToolsInspector from "@/components/RagToolsInspector";
import { Button } from "@/components/ui/button";
import { PanelRightOpen, PanelRightClose, Bot } from "lucide-react";

export default function SouschefStudio() {
  const { canExport } = useOutletContext();
  const souschef = useSouschef();
  const [showInspector, setShowInspector] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { setRefreshKey((k) => k + 1); }, [souschef.messages.length, souschef.sessionId]);

  return (
    <div className="h-full flex" data-testid="souschef-studio">
      {/* Left: sessions */}
      <div className="w-[280px] shrink-0 hidden lg:block">
        <SessionManager
          activeId={souschef.sessionId}
          onSelect={souschef.loadSession}
          onNew={souschef.newSession}
          refreshKey={refreshKey}
        />
      </div>

      {/* Center: chat */}
      <div className="flex-1 min-w-0 flex flex-col bg-[#0B0F17]">
        <div className="flex items-center justify-between px-5 h-12 border-b border-[#2A364F] shrink-0">
          <div className="flex items-center gap-2 text-slate-200">
            <Bot className="h-4 w-4 text-[#E05A2B]" />
            <span className="text-sm font-medium">SousChef AI · Estúdio de Ideação</span>
          </div>
          <Button
            data-testid="toggle-rag-inspector-button"
            variant="ghost"
            size="sm"
            onClick={() => setShowInspector((s) => !s)}
            className="text-slate-400 hover:text-[#E05A2B]"
          >
            {showInspector ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
          </Button>
        </div>
        <div className="flex-1 min-h-0">
          <ChatView
            souschef={souschef}
            canExport={canExport}
            placeholder="Descreva sua ideia, ex: Quero uma sobremesa que combine café e chocolate — calcule a sinergia."
          />
        </div>
      </div>

      {/* Right: RAG/Tools inspector */}
      <AnimatePresence initial={false}>
        {showInspector && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 360, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="shrink-0 overflow-hidden hidden md:block"
          >
            <div className="w-[360px] h-full">
              <RagToolsInspector meta={souschef.lastMeta} onClose={() => setShowInspector(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
